/**
 * Hook to fetch quick facts for a specific account by UUID.
 * Used on the Accounts tab to show inline Quick Facts on tap.
 */
import { useState, useEffect } from "react";
import { copilotApi } from "./useApi";
import type { QuickFactsData } from "../types";

interface UseQuickFactsReturn {
  data: QuickFactsData | null;
  loading: boolean;
  /** Non-null when the fetch failed (network error, 4xx/5xx). */
  error: string | null;
}

export function useQuickFacts(accountUuid: string | null): UseQuickFactsReturn {
  const [data, setData] = useState<QuickFactsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountUuid) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    copilotApi
      .get<QuickFactsData>(`/copilot/accounts/${accountUuid}/quick-facts`)
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setData(null);
          const status =
            err && typeof err === "object" && "response" in err
              ? (err as { response?: { status?: number } }).response?.status
              : undefined;
          if (status === 404) {
            setError("Account not found");
          } else if (status === 403) {
            setError("You don't have access to this account");
          } else {
            setError("Failed to load quick facts");
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountUuid]);

  return { data, loading, error };
}
